import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { fetchBilling, openBillingPortal } from '../services/billingService';
import { syncBilling } from '../services/stripeBilling';

import AccountSidebar, { ACCOUNT_TABS } from '../components/account/AccountSidebar';
import CancelSubscriptionModal from '../components/account/CancelSubscriptionModal';
import OverviewSection from '../components/account/sections/OverviewSection';
import BillingSection from '../components/account/sections/BillingSection';
import SubscriptionSection from '../components/account/sections/SubscriptionSection';
import UsageSection from '../components/account/sections/UsageSection';
import InvoicesSection from '../components/account/sections/InvoicesSection';
import SecuritySection from '../components/account/sections/SecuritySection';
import IntegrationsSection from '../components/account/sections/IntegrationsSection';
import MemorySection from '../components/account/sections/MemorySection';

import styles from '../components/account/Account.module.css';

const TAB_KEYS = ACCOUNT_TABS.map((t) => t.key);

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, signOut, getToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Active tab is URL-driven so tabs are deep-linkable (/account?tab=billing).
  const tabParam = searchParams.get('tab');
  const active = TAB_KEYS.includes(tabParam) ? tabParam : 'overview';
  const selectTab = useCallback((key) => {
    setSearchParams(key === 'overview' ? {} : { tab: key }, { replace: true });
    document.querySelector(`.${styles.main}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [setSearchParams]);

  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [logoOk, setLogoOk] = useState(true);

  const reload = useCallback(async () => {
    const data = await fetchBilling(user, { token: getToken?.() });
    setBilling(data);
    return data;
  }, [user, getToken]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const data = await fetchBilling(user, { token: getToken?.() });
      if (alive) { setBilling(data); setLoading(false); }
    })();
    return () => { alive = false; };
  }, [user, getToken]);

  // ── Confirming a payment on the way back from Stripe ───────────────────────
  //
  // Stripe redirects here with ?checkout=success. Until this existed, nothing on the return
  // trip asked Stripe whether the payment had landed: the app waited for a webhook, and if
  // that webhook failed for any reason outside our code (a stale DNS record, an expired
  // certificate, a service that was briefly down) the customer had paid and was still
  // looking at the paywall. Their money was taken and they had nothing, which is the worst
  // state this system can produce.
  //
  // So the success page now confirms the payment itself, by reading the subscription back
  // from Stripe and replaying it through the same reducer the webhook uses. The webhook
  // becomes the fast path rather than the only path, and a broken webhook costs a few
  // seconds instead of the customer's access.
  const justPaid = searchParams.get('checkout') === 'success';
  const [confirming, setConfirming] = useState(justPaid);
  const [confirmFailed, setConfirmFailed] = useState(false);

  useEffect(() => {
    if (!justPaid) return;
    let alive = true;
    (async () => {
      setConfirming(true);
      setConfirmFailed(false);
      // Stripe's own records can lag its redirect by a moment, so a single miss is not a
      // failure. A few spaced attempts covers it without making the user wait on a spinner
      // that has already succeeded.
      let ok = false;
      for (const waitMs of [0, 1500, 3000]) {
        if (!alive) return;
        if (waitMs) await new Promise((r) => setTimeout(r, waitMs));
        const res = await syncBilling({ token: getToken?.() });
        if (res?.entitled || res?.subscriptionActive) { ok = true; break; }
      }
      if (!alive) return;
      await reload();
      setConfirming(false);
      setConfirmFailed(!ok);
      // Drop the query param so a refresh does not re-run this, while keeping the tab.
      setSearchParams({ tab: 'billing' }, { replace: true });
    })();
    return () => { alive = false; };
    // Deliberately keyed on justPaid alone. getToken/reload are not referentially stable and
    // re-running this on their identity would re-confirm the same payment repeatedly.
  }, [justPaid]);

  // ── Action handlers ────────────────────────────────────────────────────────
  //
  // Every billing mutation is now one action: hand the user to the Stripe Customer Portal.
  // Cancel, resume, change plan and change interval all happen there, because that is the
  // only place they can actually take effect. Previously each of these wrote a flag to
  // localStorage and called reload(), so the page would show "Canceled" while Stripe
  // carried on charging the card.
  //
  // There is no reload() after a successful call: the Portal navigates the browser away.
  // We only stay on the page when opening it FAILED, which is the one case to report.
  const [portalError, setPortalError] = useState(null);

  const toPortal = useCallback(async () => {
    setPortalError(null);
    const res = await openBillingPortal({ token: getToken?.() });
    if (res && res.ok === false) {
      setPortalError(res.error || 'Could not open the billing portal. Please try again.');
      return false;
    }
    return true;
  }, [getToken]);

  const handleCancel = async () => {
    setCancelBusy(true);
    const ok = await toPortal();
    setCancelBusy(false);
    if (!ok) setCancelOpen(false);
  };
  const handleResume = toPortal;
  const handleSetBilling = toPortal;
  const handleSetPlan = toPortal;
  const goPricing = () => navigate('/pricing');
  const goContact = () => navigate('/contact');

  const handleLogoutAll = async () => {
    // Global scope ends every active session; falls back to a normal sign-out.
    try { await supabase?.auth?.signOut({ scope: 'global' }); } catch { /* ignore */ }
    await signOut();
    navigate('/login');
  };

  // ── Render active section ───────────────────────────────────────────────────
  function renderSection() {
    if (confirming) {
      return (
        <div className={styles.loading}>
          Confirming your payment with Stripe…
        </div>
      );
    }
    if (loading) return <div className={styles.loading}>Loading your account…</div>;
    // Shown above whichever section is open, because the failure belongs to the action the
    // user just took rather than to the section itself.
    switch (active) {
      case 'overview':
        return (
          <OverviewSection
            billing={billing}
            onNavigate={selectTab}
            onChoosePlan={goPricing}
            onContact={goContact}
          />
        );
      case 'billing':
        return (
          <BillingSection
            billing={billing}
            onChangePlan={() => selectTab('subscription')}
            onChangeBillingCycle={() => selectTab('subscription')}
            onManagePayment={toPortal}
            onViewInvoices={() => selectTab('invoices')}
            onCancel={() => setCancelOpen(true)}
            onResume={handleResume}
            onChoosePlan={goPricing}
            onContact={goContact}
          />
        );
      case 'subscription':
        return (
          <SubscriptionSection
            billing={billing}
            onSetBilling={handleSetBilling}
            onSetPlan={handleSetPlan}
            onCancel={() => setCancelOpen(true)}
            onResume={handleResume}
            onChoosePlan={goPricing}
          />
        );
      case 'usage':
        return (
          <UsageSection
            billing={billing}
            onChoosePlan={goPricing}
            onManagePlan={() => selectTab('subscription')}
          />
        );
      case 'invoices':
        return <InvoicesSection billing={billing} />;
      case 'security':
        return <SecuritySection onLogoutAll={handleLogoutAll} />;
      case 'integrations':
        return <IntegrationsSection />;
      case 'memory':
        return <MemorySection />;
      default:
        return null;
    }
  }

  return (
    <div className={styles.page}>
      {/* Header - mirrors the app's other authenticated pages */}
      <header className={styles.header}>
        <div className={styles.logo}>
          {/* Official RepBuddy logo (public/logo.png), matching LandingNav/LoginPage;
              falls back to the lettermark if the asset is missing. */}
          {logoOk ? (
            <img
              className={styles.logoImg}
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="RepBuddy"
              onError={() => setLogoOk(false)}
            />
          ) : (
            <div className={styles.logoMark}>R</div>
          )}
          <span className={styles.logoName}>RepBuddy</span>
          <span className={styles.logoDivider} />
          <span className={styles.logoSub}>Account</span>
        </div>
        <div className={styles.headerRight}>
          {user?.email && <span className={styles.headerEmail}>{user.email}</span>}
          <button className={styles.backBtn} onClick={() => navigate('/app')}>⬅️ Back to App</button>
        </div>
      </header>

      <div className={styles.shell}>
        <AccountSidebar active={active} onSelect={selectTab} identity={billing?.identity} />
        <main className={styles.main}>{renderSection()}</main>
      </div>

      <CancelSubscriptionModal
        open={cancelOpen}
        billing={billing}
        busy={cancelBusy}
        onConfirm={handleCancel}
        onClose={() => setCancelOpen(false)}
      />
    </div>
  );
}
